import { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import { BestPracticesSetting } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSetting'
import { compareBestPracticesMutation } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSettingUtil'
import { useBestPracticesMutation } from 'components/WorkspaceEditor/api/useBestPracticesMutation'
import { updateBestPracticesForContentItem } from 'components/WorkspaceEditor/features/bestPractices'
import {
    useBestPracticeElementData,
    useHoveredItemActions,
    useIsBestPracticeElementDataLoading,
    useIsProjectDataLoading,
    useProjectData,
    useSelectedElementId,
    useSelectedMainMenuNode,
} from 'components/WorkspaceEditor/HoveredItemStore'
import {
    useSelectedProjectElementNode,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { BestPracticeDataExtended } from 'pages/ProjectsPage/WorkspacePage/components/Common/BestPracticeSyncTypes'
import React, { useMemo } from 'react'
import {
    WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY,
    WorkspaceManufacturerListingItem,
} from 'shared/utils/workspaceUtils'
import { useBestPracticesKeyTrap } from './hooks/useBestPracticesKeyTrap'
import { useContentItemElements } from './hooks/useContentItemElements'

/**
 * Represents the complete configuration for a menu item, extending the base MenuItemConfig.
 */
export interface CompleteMenuItemConfig extends MenuItemConfig {
    /** Optional flag indicating whether the menu item is disabled. */
    isDisabled?: boolean
    /** Optional flag indicating whether the menu item is hidden. */
    isHidden?: boolean
    /** Optional small label text for the menu item. */
    smallLabel?: string
}

/** Extended Best Practice input with element ID for SelectionOption pills */
interface BestPracticeMutationInput
    extends Omit<Partial<BestPracticeDataExtended>, 'elements'> {
    contentItemElementId?: string
    /** Elements can be from getRecursiveElementsForNode or standard elements */
    elements?: unknown[]
}

/** Props for the BestPracticesSettingPopover component. */
export interface BestPracticesSettingPopoverProps {
    /** The element to which the popover is anchored. */
    anchorElement: Element | null
    /** Indicates if the current setting is a best practice. */
    isBestPractice: boolean
    /** Indicates if the current setting is a global best practice. */
    isGlobalBestPractice: boolean
    /** Indicates if editing the best practice setting is disabled. */
    isBestPracticeEditDisabled: boolean
    /** Callback function to close the main menu. */
    onCloseMainMenu?: () => void
}

export const BestPracticesSettingPopover: React.FC<BestPracticesSettingPopoverProps> = ({
    anchorElement,
    isBestPractice,
    isGlobalBestPractice,
    isBestPracticeEditDisabled,
    onCloseMainMenu,
}) => {
    const bestPracticeElementData = useBestPracticeElementData()
    const projectData = useProjectData()
    const isProjectDataLoading = useIsProjectDataLoading()
    const isBestPracticeElementDataLoading = useIsBestPracticeElementDataLoading()
    const { setIsBPMenuDisplayed } = useHoveredItemActions()
    const selectedMainMenuNode = useSelectedMainMenuNode()
    const selectedElementId = useSelectedElementId()
    const selectedProjectElementNode = useSelectedProjectElementNode()

    const {
        addUpdateBestPractice,
        removeBestPractice,
    } = useBestPracticesMutation()

    useBestPracticesKeyTrap()

    const {
        getRecursiveElementsForNode,
        getElementsForNode,
    } = useContentItemElements()

    const isLoading = useMemo(() => {
        return isBestPracticeElementDataLoading || isProjectDataLoading
    }, [isBestPracticeElementDataLoading, isProjectDataLoading])

    const handleBestPracticesMutationData = React.useCallback(
        (mutationData: Partial<BestPracticeData> | null) => {
            // Get the latest state from the store for optimistic update
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes
            const contentItemId = selectedMainMenuNode?.id
            const isHyperLink =
                selectedMainMenuNode?.type?.toLowerCase() === 'hyperlink'
            // Read full node from store — selectedMainMenuNode may only have { id, type }
            // when opened via openBPMenu, missing isHidden/isUserAdded
            const contentItemNode = latestNodes.find((item) =>
                isHyperLink
                    ? item.pillId === contentItemId
                    : item.id === contentItemId
            )
            // Use selectedElementId from store (set when opening BP menu for SelectionOption pills)
            const elementId = isHyperLink
                ? contentItemNode?.pillId
                : selectedElementId
            // Check if this is a manufacturer pill (Hyperlink with elementId)
            const isManufacturerPill =
                elementId !== undefined &&
                (contentItemNode?.isManufacturers || contentItemNode?.isBOD)

            // Notify the Products Listing dialog via service worker to update cards
            const notifyListingDialogOfBPChange = (
                isBP: boolean,
                isGBP: boolean
            ) => {
                if (!isManufacturerPill || !elementId) return
                navigator.serviceWorker?.controller?.postMessage({
                    [WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]: {
                        listingId: elementId,
                        isBP,
                        isGBP,
                    } as WorkspaceManufacturerListingItem,
                })
            }
            if (mutationData) {
                // If there's no existing BP data (getBestPractice is null), we should create a new one
                // Otherwise, check if the mutation data has changed
                const hasExistingBP =
                    bestPracticeElementData?.getBestPractice?.id
                const isMutationUpdated =
                    !hasExistingBP ||
                    compareBestPracticesMutation(
                        bestPracticeElementData,
                        projectData?.getProject,
                        mutationData
                    )

                if (isMutationUpdated && contentItemId) {
                    const defaultInput: BestPracticeMutationInput = {
                        ...mutationData,
                        parentProjectId: projectData?.getProject?.id,
                        ...(isManufacturerPill
                            ? {
                                // For manufacturer pills, use listingId (EXACT copy from ProjectProductListing.tsx)
                                listingId: elementId,
                                familyNumber: selectedProjectElementNode?.parentTreePath
                                    ?.split('||')
                                    ?.pop()
                                    ?.split('|')[0],
                                productType: selectedProjectElementNode?.text,
                                projectElementNodeId:
                                    selectedProjectElementNode?.id,
                            }
                            : {
                                // For regular pills, use contentItemId
                                contentItemId,
                                contentItemElementId: elementId,
                            }),
                    }

                    if (!isManufacturerPill) {
                        defaultInput.projectElementNodeId =
                            selectedProjectElementNode?.id
                    }

                    // Add Smart Project Copy specific fields
                    if (process.env.REACT_APP_EnableSmartProjectCopy === '1') {
                        // IMPORTANT: Don't include elements when elementId is present
                        // elementId means we're setting BP on a pill (SelectionOption) within an OptionGroup
                        // In this case, we don't want to include the OptionGroup's elements (pills) in the mutation
                        // This matches the behavior in ContentItemElementText.tsx
                        if (!elementId) {
                            // Use recursive elements ONLY for user-added content
                            const isUserAdded =
                                contentItemNode?.isUserAdded ?? false
                            defaultInput.elements = isUserAdded
                                ? getRecursiveElementsForNode(contentItemId)
                                : getElementsForNode(contentItemId)
                            defaultInput.isUserAddedContentBestPractice = isUserAdded
                        }
                        defaultInput.isHidden =
                            contentItemNode?.isHidden ?? false
                    }

                    const mutationInput =
                        bestPracticeElementData?.getBestPractice?.id &&
                            bestPracticeElementData?.getBestPractice
                                ?.parentProjectId === projectData?.getProject?.id
                            ? {
                                ...defaultInput,
                                id:
                                    bestPracticeElementData.getBestPractice
                                        .id,
                            }
                            : defaultInput

                    // Apply optimistic update immediately
                    // If elementId exists, update the element within content item's elements array
                    // Otherwise, update the content item node itself
                    // Logic: If GBP, only set isGlobalBestPractice; if BP, only set isBestPractice
                    const isGlobal = mutationData.isGlobal ?? false
                    const updatedNodes = updateBestPracticesForContentItem(
                        contentItemId,
                        elementId,
                        latestNodes,
                        !isGlobal, // isBestPractice: true only when NOT global
                        isGlobal, // isGlobalBestPractice: true only when global.
                        isManufacturerPill
                    )
                    storeState.actions.setContentItemViewNodes(updatedNodes)
                    notifyListingDialogOfBPChange(!isGlobal, isGlobal)

                    addUpdateBestPractice({
                        variables: {
                            input: mutationInput,
                        },
                    }).then(
                        () => {
                            useWorkspace2Store.getState().actions.triggerBPSyncCheck?.()
                        },
                        (error) => {
                            // Revert workspace + listing dialog to original state on error
                            storeState.actions.setContentItemViewNodes(latestNodes)
                            notifyListingDialogOfBPChange(
                                isBestPractice,
                                isGlobalBestPractice
                            )
                        }
                    )
                }
            } else {
                // Remove best practice
                if (
                    bestPracticeElementData?.getBestPractice?.id &&
                    contentItemId
                ) {
                    // Apply optimistic update immediately (remove BP flags)
                    const updatedNodes = updateBestPracticesForContentItem(
                        contentItemId,
                        elementId,
                        latestNodes,
                        false,
                        false,
                        isManufacturerPill
                    )
                    storeState.actions.setContentItemViewNodes(updatedNodes)
                    notifyListingDialogOfBPChange(false, false)

                    removeBestPractice({
                        variables: {
                            input: {
                                id: bestPracticeElementData.getBestPractice.id,
                                // Add isHidden for Smart Project Copy
                                // Cast to any because BestPracticeData type doesn't include isHidden
                                isHidden: contentItemNode?.isHidden ?? false,
                            } as Partial<BestPracticeData>,
                        },
                    }).catch((error) => {
                        console.error('Failed to remove best practice:', error)
                        // Revert workspace + listing dialog to original state on error
                        storeState.actions.setContentItemViewNodes(latestNodes)
                        notifyListingDialogOfBPChange(
                            isBestPractice,
                            isGlobalBestPractice
                        )
                    })
                }
            }
            setIsBPMenuDisplayed(false)
            onCloseMainMenu?.()
        },
        [
            bestPracticeElementData,
            projectData,
            selectedMainMenuNode,
            selectedElementId,
            selectedProjectElementNode,
            addUpdateBestPractice,
            removeBestPractice,
            setIsBPMenuDisplayed,
            onCloseMainMenu,
            getRecursiveElementsForNode,
        ]
    )
    return (
        <BestPracticesSetting
            // key={`BP-${template?.id}`}
            popoverAnchor={anchorElement}
            onlyPopover
            projectDetail={projectData?.getProject}
            setBestPracticesMutationData={handleBestPracticesMutationData}
            showBestPracticesIcons={false}
            data={bestPracticeElementData}
            loading={isLoading}
            isBestPractice={isBestPractice}
            isGlobalBestPractice={isGlobalBestPractice}
            isDisabled={isBestPracticeEditDisabled}
        />
    )
}
